/**
 * Coordinate conversion utilities.
 *
 * The eSanza database stores coordinates in several formats:
 *   - { lat, lng }                          (farmers, cohorts)
 *   - { latitude, longitude }               (some legacy rows)
 *   - [lat, lng] array                      (some raw DB rows)
 *   - JSON string of the above              (all of the above, stringified)
 *   - PostGIS POINT(lng lat)                (rare)
 *   - GeoJSON [lng, lat]                    (target format)
 *
 * This module centralises all conversions so the rest of the codebase
 * only deals with GeoJSON [longitude, latitude].
 */

import type { GeoCoord, LatLng } from '../types/mapTypes';
import { RWANDA_BOUNDS } from '../config/mapboxConfig';

// ─── Core: parse any input to GeoCoord ──────────────────────────────

/**
 * Parse a single coordinate from the various DB shapes into GeoJSON [lng, lat].
 * Returns null for invalid or missing data.
 */
export function toGeoCoord(
  input: unknown,
  arrayOrder: 'lat-lng' | 'lng-lat' = 'lat-lng',
): GeoCoord | null {
  if (input == null) return null;

  // Already a GeoJSON [lng, lat]
  if (Array.isArray(input) && input.length >= 2) {
    const a = Number(input[0]);
    const b = Number(input[1]);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      const coord: GeoCoord = arrayOrder === 'lng-lat' ? [a, b] : [b, a];
      return isValidCoord(coord) ? coord : null;
    }
    return null;
  }

  // Object with lat/lng or latitude/longitude
  if (typeof input === 'object') {
    const obj = input as Record<string, unknown>;
    // Try explicit keys
    const latVal = Number(obj.lat ?? obj.latitude);
    const lngVal = Number(obj.lng ?? obj.longitude ?? obj.lon);
    if (Number.isFinite(latVal) && Number.isFinite(lngVal)) {
      return [lngVal, latVal]; // [lng, lat]
    }
    return null;
  }

  // String: try JSON parse, then PostGIS POINT format
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return null;

    // PostGIS POINT(lng lat)
    const pointMatch = trimmed.match(/POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i);
    if (pointMatch) {
      const lng = Number(pointMatch[1]);
      const lat = Number(pointMatch[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return [lng, lat];
    }

    // Try JSON parse
    try {
      const parsed = JSON.parse(trimmed);
      return toGeoCoord(parsed, arrayOrder);
    } catch {
      // Not JSON
    }

    // Try "lat,lng" or "lng,lat" comma-separated
    const parts = trimmed.split(',').map((s) => Number(s.trim()));
    if (parts.length === 2 && parts.every(Number.isFinite)) {
      const coord: GeoCoord = arrayOrder === 'lng-lat'
        ? [parts[0], parts[1]]
        : [parts[1], parts[0]];
      return isValidCoord(coord) ? coord : null;
    }
  }

  return null;
}

/**
 * Parse a boundary (array of coordinates) from DB into GeoCoord[].
 * Also repairs boundaries that were stored with swapped lat/lng semantics
 * (e.g. stored as [lat-holding-lng, lng-holding-lat] by an older backend
 * heuristic that misread [lng, lat] arrays for African coordinates whose
 * longitude happens to fall inside the latitude range).
 */
export function toBoundary(input: unknown): GeoCoord[] {
  if (!Array.isArray(input)) return [];

  const result: GeoCoord[] = [];
  for (const item of input) {
    const coord = toGeoCoord(item);
    if (coord) result.push(coord);
  }
  return result.length >= 2 ? repairSwappedBoundary(result) : result;
}

/**
 * Detect and fix a boundary whose lat/lng values were stored swapped.
 *
 * A GeoJSON [lng, lat] boundary lives in Rwanda (lat ≈ -1..-3, lng ≈ 29..31).
 * If the raw record was mis-saved such that the majority of points carry a
 * swapped signature — and swapping them back lands inside the Rwanda bounding
 * box — the values are reversed by the legacy backend heuristic.
 */
export function repairSwappedBoundary(boundary: GeoCoord[]): GeoCoord[] {
  if (boundary.length === 0) return boundary;

  let swappedVotes = 0;
  for (const [lng, lat] of boundary) {
    if (Math.abs(lat) > 10 && Math.abs(lng) <= 10) swappedVotes += 1;
  }
  const majoritySwapped = swappedVotes > boundary.length / 2;
  if (!majoritySwapped) return boundary;

  const repaired = boundary.map(([lng, lat]) => [lat, lng] as GeoCoord);
  if (boundaryWithinBounds(repaired, RWANDA_BOUNDS)) return repaired;
  return boundary;
}

/** True when every point in the boundary lies within the given bbox [w, s, e, n]. */
function boundaryWithinBounds(
  boundary: GeoCoord[],
  bounds: [number, number, number, number],
): boolean {
  if (boundary.length === 0) return false;
  const [w, s, e, n] = bounds;
  return boundary.every(([lng, lat]) => lng >= w && lng <= e && lat >= s && lat <= n);
}

/**
 * Parse a boundary that might be a JSON string.
 */
export function parseBoundaryMaybeString(input: unknown): GeoCoord[] {
  if (typeof input === 'string') {
    try {
      return toBoundary(JSON.parse(input));
    } catch {
      return [];
    }
  }
  return toBoundary(input);
}

/**
 * Ensure center is a GeoCoord. Falls back to computing from boundary.
 */
export function ensureCenter(
  centerInput: unknown,
  boundary: GeoCoord[],
): GeoCoord | null {
  const direct = toGeoCoord(centerInput, 'lng-lat');
  if (direct) {
    // Center may carry the same legacy lat/lng swap as the boundary. If the
    // stored center clearly holds swapped magnitudes — and correcting it puts
    // it inside the Rwanda operational area — reverse it.
    if (Math.abs(direct[1]) > 10 && Math.abs(direct[0]) <= 10) {
      const repaired = [direct[1], direct[0]] as GeoCoord;
      if (boundaryWithinBounds([repaired], RWANDA_BOUNDS)) return repaired;
    }
    return direct;
  }

  if (boundary.length === 0) return null;
  return centerFromBoundary(boundary);
}

/**
 * Compute centroid of a boundary (simple average).
 */
export function centerFromBoundary(boundary: GeoCoord[]): GeoCoord {
  if (boundary.length === 0) return [30.0619, -1.9441]; // Kigali default

  let sumLng = 0;
  let sumLat = 0;
  for (const [lng, lat] of boundary) {
    sumLng += lng;
    sumLat += lat;
  }
  return [sumLng / boundary.length, sumLat / boundary.length];
}

// ─── Conversion: GeoCoord ↔ LatLng (for legacy components) ─────────

/** GeoCoord [lng, lat] → LatLng { lat, lng } */
export function toLatLng(coord: GeoCoord): LatLng {
  return { lat: coord[1], lng: coord[0] };
}

/** LatLng { lat, lng } → GeoCoord [lng, lat] */
export function fromLatLng(latLng: LatLng): GeoCoord {
  return [latLng.lng, latLng.lat];
}

/** Convert GeoCoord[] → LatLng[] */
export function toLatLngArray(coords: GeoCoord[]): LatLng[] {
  return coords.map(toLatLng);
}

/** Convert LatLng[] → GeoCoord[] */
export function fromLatLngArray(latlngs: LatLng[]): GeoCoord[] {
  return latlngs.map(fromLatLng);
}

// ─── Validation ─────────────────────────────────────────────────────

/**
 * Validate that a GeoCoord is finite and within reasonable bounds.
 */
export function isValidCoord(coord: GeoCoord): boolean {
  if (!coord || coord.length < 2) return false;
  const [lng, lat] = coord;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  if (lat < -90 || lat > 90) return false;
  if (lng < -180 || lng > 180) return false;
  return true;
}

/**
 * Filter and validate a boundary, removing invalid points.
 */
export function validBoundary(boundary: GeoCoord[]): GeoCoord[] {
  return boundary.filter(isValidCoord);
}

/**
 * Calculate area in hectares from a GeoJSON [lng, lat] polygon.
 * Uses the Shoelace formula with approximate meter conversion.
 */
export function calculateAreaHectares(polygon: GeoCoord[]): number {
  if (polygon.length < 3) return 0;

  // Use the average latitude for meter conversion
  const avgLat =
    polygon.reduce((sum, [, lat]) => sum + lat, 0) / polygon.length;
  const metersPerLat = 111320;
  const metersPerLng = 111320 * Math.cos((avgLat * Math.PI) / 180);

  let area = 0;
  for (let i = 0; i < polygon.length; i++) {
    const [lng1, lat1] = polygon[i];
    const [lng2, lat2] = polygon[(i + 1) % polygon.length];
    area += lng1 * metersPerLng * lat2 * metersPerLat;
    area -= lng2 * metersPerLng * lat1 * metersPerLat;
  }
  return Math.abs(area) / 2 / 10000; // m² → hectares
}

// ─── Internal helpers ───────────────────────────────────────────────
